import React from 'react';
import { useLanguage } from '../i18n/LanguageContext';

const rules = {
  fr: { title: 'Règlement de la ligue de padel', sections: [['Durée des matchs', 'Chaque match dure 1h30 maximum. S’il ne peut pas se terminer dans ce délai, il se termine en l’état.'], ['Format des sets', 'Les matchs se jouent en 2 sets gagnants de 6 jeux.'], ['Fin du match', 'Victoire 2-0 : 3 points au vainqueur et 1 au perdant. À 1 set partout sans possibilité de terminer : match nul et 2 points par équipe. L’équipe menant au troisième set bénéficie d’un avantage en cas d’égalité au classement.'], ['Critères de classement', 'Ordre des critères : points, différence de sets, nombre de jeux puis confrontation directe.'], ['Communication des résultats', 'Chaque équipe encode le résultat sur le site avec le code fourni, en accord avec l’adversaire. La limite est le mardi à 12h00. Après deux manquements, l’équipe est déclarée perdante par défaut.']], privacy: 'Politique de confidentialité', legal: 'Mentions légales' },
  de: { title: 'Regeln der Padel-Liga', sections: [['Spieldauer', 'Jedes Spiel dauert höchstens 1 Stunde 30 Minuten. Kann es nicht beendet werden, gilt der aktuelle Stand.'], ['Satzformat', 'Gespielt wird auf zwei Gewinnsätze mit je sechs Spielen.'], ['Spielende', 'Bei 2:0 erhält der Sieger 3 Punkte, der Verlierer 1 Punkt. Bei 1:1 ohne Abschluss erhält jedes Team 2 Punkte. Eine Führung im dritten Satz dient bei Ranggleichheit als Vorteil.'], ['Ranglistenkriterien', 'Reihenfolge: Punkte, Satzdifferenz, Anzahl der Spiele und direkter Vergleich.'], ['Ergebnismeldung', 'Jedes Team trägt das abgestimmte Ergebnis mit dem Zugangscode ein. Frist: Dienstag 12:00 Uhr. Nach zwei Verstößen verliert das Team kampflos.']], privacy: 'Datenschutz', legal: 'Impressum' },
  nl: { title: 'Reglement van de padelcompetitie', sections: [['Duur van de wedstrijden', 'Elke wedstrijd duurt maximaal 1u30. Kan ze niet worden uitgespeeld, dan eindigt ze met de huidige stand.'], ['Setformaat', 'De wedstrijden worden gespeeld naar twee gewonnen sets van zes games.'], ['Einde van de wedstrijd', 'Bij 2-0 krijgt de winnaar 3 punten en de verliezer 1 punt. Bij 1-1 zonder mogelijkheid om te eindigen krijgt elk team 2 punten. Een voorsprong in de derde set geeft voordeel bij een gelijke ranking.'], ['Rankingcriteria', 'Volgorde: punten, setverschil, aantal games en onderlinge wedstrijd.'], ['Resultaten doorgeven', 'Elk team voert het overeengekomen resultaat in met de toegangscode. Deadline: dinsdag 12.00 uur. Na twee inbreuken verliest het team forfait.']], privacy: 'Privacybeleid', legal: 'Wettelijke vermeldingen' },
  en: { title: 'Padel league rules', sections: [['Match duration', 'Each match lasts a maximum of 1 hour 30 minutes. If it cannot be completed, the current score stands.'], ['Set format', 'Matches are played as the best of three six-game sets.'], ['End of the match', 'At 2-0, the winner receives 3 points and the loser 1. At 1-1 when play cannot finish, each team receives 2 points. A third-set lead gives an advantage in a ranking tie.'], ['Ranking criteria', 'Order: points, set difference, games won/lost, then head-to-head result.'], ['Reporting results', 'Each team enters the agreed result using its access code. Deadline: Tuesday at noon. After two failures, the team loses by default.']], privacy: 'Privacy policy', legal: 'Legal notice' },
};

const ReglementLigue = () => {
  const { language } = useLanguage();
  const c = rules[language] || rules.fr;
  return (
    <section className="bg-white text-black min-h-screen flex flex-col justify-between">
      <div className="w-full max-w-screen-lg mx-auto text-center flex-grow pt-24 pb-12 px-4 sm:px-6 md:px-10 lg:px-16">
        <h2 className="text-3xl md:text-4xl font-semibold text-[#4c2f8e] mb-10">
          {c.title}
        </h2>

        <div className="bg-[#f9f9f9] shadow-lg rounded-xl border border-gray-300 p-6 md:p-10 text-left">
          <div className="hidden"><h3 className="text-xl font-semibold text-[#4c2f8e] mb-4">
            Durée des matchs
          </h3>
          <p className="text-gray-700 mb-4">
            Chaque match dure <strong>1H30 maximum</strong>. Si le match ne peut pas se terminer dans les 1H30, il se termine en l’état.
          </p>

          <h3 className="text-xl font-semibold text-[#4c2f8e] mb-4">
            Format des sets
          </h3>
          <p className="text-gray-700 mb-4">
            Les matchs se jouent en <strong>2 sets gagnants de 6 jeux</strong>.
          </p>

          <h3 className="text-xl font-semibold text-[#4c2f8e] mb-4">
            Fin du match
          </h3>
          <ul className="text-gray-700 list-disc list-inside mb-4">
            <li>
              Si une équipe gagne 2 sets à 0, elle remporte <strong>3 points</strong> et l’équipe perdante <strong>1 point</strong>.
            </li>
            <li>
              Si le score est de 1 set partout et que le match ne peut pas se terminer, c’est un <strong>match nul</strong>, et chaque équipe remporte <strong>2 points</strong>.
            </li>
            <li>
              L’équipe menant dans le 3ème set au moment de l’arrêt du match aura un <strong>avantage au classement</strong> en cas d’égalité.
            </li>
          </ul>

          <h3 className="text-xl font-semibold text-[#4c2f8e] mb-4">
            Critères de Classement
          </h3>
          <ul className="text-gray-700 list-disc list-inside mb-4">
            <li><strong>Points</strong> : Le classement est déterminé par le nombre de points.</li>
            <li><strong>Différence de sets</strong> : En cas d’égalité de points, la différence de sets remportés/perdus est prise en compte.</li>
            <li><strong>Nombre de jeux</strong> : Si l’égalité persiste, le nombre de jeux gagnés/perdus est considéré.</li>
            <li><strong>Rencontre directe</strong> : En cas d’égalité parfaite, la rencontre directe entre les deux équipes égalitées détermine le classement.</li>
          </ul>

          <h3 className="text-xl font-semibold text-[#4c2f8e] mb-4">
            Communication des Résultats
          </h3>
          <p className="text-gray-700">
            Chaque équipe doit encoder son résultat directement sur le site de la ligue en utilisant le code d’accès fourni. Il est recommandé de le faire immédiatement après le match, en accord avec l’équipe adverse. Les résultats peuvent être encodés jusqu’au mardi à 12h00. En cas de deux manquements à cette règle, l’équipe sera déclarée perdante par défaut.
          </p></div>
          {c.sections.map(([title, text]) => <div key={title} className="mb-6"><h3 className="text-xl font-semibold text-[#4c2f8e] mb-2">{title}</h3><p className="text-gray-700">{text}</p></div>)}
        </div>
      </div>

      <footer className="bg-white border-t border-[#4c2f8e] shadow-sm py-4 mt-8">
        <div className="w-full max-w-screen-lg mx-auto flex flex-col md:flex-row justify-between items-center px-6">
        <div className="flex justify-center mb-4 md:mb-0 space-x-4">
  <a href="https://www.boisduloup.be/" target="_blank" rel="noopener noreferrer">
    <img 
      src="/BDL.jfif" 
      alt="Tennis Club Bois du Loup" 
      className="h-10 w-10 transition-transform duration-300 hover:scale-105" 
    />
  </a>
  <a href="https://www.facebook.com/RETCJUMET" target="_blank" rel="noopener noreferrer">
    <img 
      src="/JUMET.jfif" 
      alt="RETC Jumet" 
      className="h-10 w-10 transition-transform duration-300 hover:scale-105" 
    />
  </a>
  <a href="https://www.facebook.com/p/333-Padel-61556247308483/" target="_blank" rel="noopener noreferrer">
    <img 
      src="/333.jfif" 
      alt="333 Padel" 
      className="h-10 w-10 rounded-full transition-transform duration-300 hover:scale-105" 
    />
  </a>
  <a href="https://www.facebook.com/Centre.sportif.Adeps.La.Mosane/?locale=fr_FR" target="_blank" rel="noopener noreferrer">
    <img 
      src="/adeps.jfif" 
      alt="Adeps Le Mosane" 
      className="h-10 w-10 rounded-full transition-transform duration-300 hover:scale-105" 
    />
  </a>
  <a href="https://www.facebook.com/p/Hall-Omnisports-de-Grez-Doiceau-100063821912059/" target="_blank" rel="noopener noreferrer">
    <img 
      src="/grez.jfif" 
      alt="Grez-Doiceau" 
      className="h-10 w-10 rounded-full transition-transform duration-300 hover:scale-105" 
    />
  </a>
  <a href="http://www.padel-one.be/" target="_blank" rel="noopener noreferrer">
    <img 
      src="/padelone.jfif" 
      alt="Padel One" 
      className="h-10 w-10 rounded-full transition-transform duration-300 hover:scale-105" 
    />
  </a>
</div>


          <div className="flex space-x-4 text-sm text-gray-700">
            <a 
              href="/privacy-policy" 
              className="hover:text-[#4c2f8e] transition duration-300 hover:underline hover:underline-offset-4"
            >
              {c.privacy}
            </a>
            <a 
              href="/legal-mentions" 
              className="hover:text-[#4c2f8e] transition duration-300 hover:underline hover:underline-offset-4"
            >
              {c.legal}
            </a>
          </div>
        </div>
      </footer>
    </section>
  );
};

export default ReglementLigue;
